// UrlFieldValue.js — structured URL field value helpers (client)

import { isHttpUrl, normalizeUrlForOpen } from 'utilities/Url';

export const URL_FETCH_STATUS = Object.freeze({
  IDLE: 'idle',
  PENDING: 'pending',
  FETCHING: 'fetching',
  READY: 'ready',
  FAILED: 'failed',
});

function cleanString(value) {
  return String(value ?? '').trim();
}

export function getUrlString(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return cleanString(value);
  }
  if (value && typeof value === 'object') {
    return cleanString(value.url);
  }
  return '';
}

export function normalizeUrlFieldValue(value, { defaultIndexContent = false } = {}) {
  if (value == null || value === '') return null;

  if (typeof value === 'string' || typeof value === 'number') {
    const url = cleanString(value);
    if (!url) return null;
    return {
      url,
      title: null,
      summary: null,
      canonicalUrl: null,
      indexContent: defaultIndexContent === true,
      fetchStatus: URL_FETCH_STATUS.IDLE,
      fetchedAt: null,
      fetchError: null,
      extractId: null,
      lastFetchedUrl: null,
    };
  }

  if (typeof value !== 'object' || Array.isArray(value)) return null;

  const url = cleanString(value.url);
  if (!url) return null;

  return {
    url,
    title: value.title == null ? null : cleanString(value.title) || null,
    summary: value.summary == null ? null : cleanString(value.summary) || null,
    canonicalUrl: value.canonicalUrl == null ? null : cleanString(value.canonicalUrl) || null,
    indexContent: value.indexContent === true,
    fetchStatus: cleanString(value.fetchStatus).toLowerCase() || URL_FETCH_STATUS.IDLE,
    fetchedAt: value.fetchedAt ?? null,
    fetchError: value.fetchError == null ? null : cleanString(value.fetchError) || null,
    extractId: value.extractId == null ? null : cleanString(value.extractId) || null,
    lastFetchedUrl: value.lastFetchedUrl == null ? null : cleanString(value.lastFetchedUrl) || null,
  };
}

export function buildUrlFieldValueFromInput(nextUrl, priorValue = null, {
  defaultIndexContent = false,
} = {}) {
  const url = cleanString(nextUrl);
  if (!url) return null;

  const prior = normalizeUrlFieldValue(priorValue, { defaultIndexContent });
  const priorHref = prior?.url || '';
  const sameIdentity = priorHref
    && normalizeUrlForOpen(priorHref) === normalizeUrlForOpen(url);

  if (sameIdentity && prior) {
    return {
      ...prior,
      url,
    };
  }

  return {
    url,
    title: null,
    summary: null,
    canonicalUrl: null,
    indexContent: prior ? prior.indexContent === true : defaultIndexContent === true,
    fetchStatus: URL_FETCH_STATUS.IDLE,
    fetchedAt: null,
    fetchError: null,
    extractId: null,
    lastFetchedUrl: null,
  };
}

export function formatUrlFieldDisplay(value) {
  const normalized = normalizeUrlFieldValue(value);
  if (!normalized) return getUrlString(value);
  if (normalized.title && normalized.url) return `${normalized.title} — ${normalized.url}`;
  return normalized.title || normalized.url || '';
}

export function isValidUrlFieldInput(value) {
  const href = getUrlString(value);
  return href === '' || isHttpUrl(href) || isHttpUrl(normalizeUrlForOpen(href));
}
